import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight, CalendarDays, Search, X, Clock, Phone, PhoneCall, GripVertical, ListChecks, CalendarPlus, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";

const DAGEN = ["ma", "di", "wo", "do", "vr", "za", "zo"];
const MAANDEN = ["jan", "feb", "mrt", "apr", "mei", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];
const MAANDEN_VOL = ["januari", "februari", "maart", "april", "mei", "juni", "juli", "augustus", "september", "oktober", "november", "december"];
const HOUR_H = 50;            // pixels per uur in dag/week-weergave
const MIN_BLOCK = 22;        // minimale hoogte van een afspraakblok
const TIJD_SLOTS = (() => { const a = []; for (let h = 6; h <= 20; h++) { for (const m of [0, 15, 30, 45]) a.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`); } return a; })();
const INTERVALLEN = [5, 10, 15, 20, 30, 45, 60];
const dagLabel = (ymd) => { if (!ymd) return "Kies dag"; const d = new Date(`${ymd}T00:00:00`); return d.toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "long" }); };

function startOfWeek(d) { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); x.setHours(0, 0, 0, 0); return x; }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function addMonths(d, n) { const x = new Date(d); x.setMonth(x.getMonth() + n); return x; }
function startOfMonth(d) { const x = new Date(d); x.setDate(1); x.setHours(0, 0, 0, 0); return x; }
function isoWeek(d) {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = (date.getUTCDay() + 6) % 7;
  date.setUTCDate(date.getUTCDate() - dayNum + 3);
  const firstThursday = new Date(Date.UTC(date.getUTCFullYear(), 0, 4));
  return 1 + Math.round((date - firstThursday) / 604800000);
}
const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const minutesOf = (d) => d.getHours() * 60 + d.getMinutes();

const FASE = {
  "Cold": { dot: "bg-slate-400", bar: "bg-slate-400", bg: "bg-slate-50", tx: "text-slate-700", bd: "border-slate-200" },
  "Terugbellen": { dot: "bg-amber-500", bar: "bg-amber-500", bg: "bg-amber-50", tx: "text-amber-900", bd: "border-amber-200" },
  "Sample opsturen": { dot: "bg-sky-500", bar: "bg-sky-500", bg: "bg-sky-50", tx: "text-sky-900", bd: "border-sky-200" },
  "Sample opgestuurd": { dot: "bg-indigo-500", bar: "bg-indigo-500", bg: "bg-indigo-50", tx: "text-indigo-900", bd: "border-indigo-200" },
  "Onderhandeling": { dot: "bg-violet-500", bar: "bg-violet-500", bg: "bg-violet-50", tx: "text-violet-900", bd: "border-violet-200" },
  "Deal!": { dot: "bg-emerald-500", bar: "bg-emerald-500", bg: "bg-emerald-50", tx: "text-emerald-900", bd: "border-emerald-200" },
  "Afgevallen": { dot: "bg-rose-500", bar: "bg-rose-500", bg: "bg-rose-50", tx: "text-rose-900", bd: "border-rose-200" },
  "Beltaak": { dot: "bg-cyan-600", bar: "bg-cyan-600", bg: "bg-cyan-50", tx: "text-cyan-900", bd: "border-cyan-200" },
};
const EXTRA_KLEUREN = [
  { dot: "bg-teal-500", bar: "bg-teal-500", bg: "bg-teal-50", tx: "text-teal-900", bd: "border-teal-200" },
  { dot: "bg-orange-500", bar: "bg-orange-500", bg: "bg-orange-50", tx: "text-orange-900", bd: "border-orange-200" },
  { dot: "bg-lime-600", bar: "bg-lime-600", bg: "bg-lime-50", tx: "text-lime-900", bd: "border-lime-200" },
  { dot: "bg-fuchsia-500", bar: "bg-fuchsia-500", bg: "bg-fuchsia-50", tx: "text-fuchsia-900", bd: "border-fuchsia-200" },
  { dot: "bg-blue-500", bar: "bg-blue-500", bg: "bg-blue-50", tx: "text-blue-900", bd: "border-blue-200" },
  { dot: "bg-pink-500", bar: "bg-pink-500", bg: "bg-pink-50", tx: "text-pink-900", bd: "border-pink-200" },
  { dot: "bg-stone-500", bar: "bg-stone-500", bg: "bg-stone-50", tx: "text-stone-800", bd: "border-stone-200" },
];
const _hash = (s) => { let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0; return h; };
const fc = (f) => FASE[f] || EXTRA_KLEUREN[_hash(f || "") % EXTRA_KLEUREN.length];
const FASES = ["Cold", "Terugbellen", "Sample opsturen", "Sample opgestuurd", "Onderhandeling", "Deal!", "Beltaak"];

// Overlappende afspraken naast elkaar leggen (kolom-packing, zoals Apple Agenda)
function packEvents(items) {
  const evs = items.map((e) => ({ ...e })).sort((a, b) => a._start - b._start || a._end - b._end);
  let group = [], groupEnd = -1;
  const flush = () => {
    const colEnds = [];
    group.forEach((ev) => {
      let placed = false;
      for (let i = 0; i < colEnds.length; i++) {
        if (colEnds[i] <= ev._start) { ev._col = i; colEnds[i] = ev._end; placed = true; break; }
      }
      if (!placed) { ev._col = colEnds.length; colEnds.push(ev._end); }
    });
    group.forEach((ev) => { ev._cols = colEnds.length; });
    group = []; groupEnd = -1;
  };
  evs.forEach((ev) => {
    if (group.length && ev._start >= groupEnd) flush();
    group.push(ev);
    groupEnd = Math.max(groupEnd, ev._end);
  });
  flush();
  return evs;
}

export default function CampagnePlanner() {
  const navigate = useNavigate();
  const [view, setView] = useState("week");
  const [anchor, setAnchor] = useState(() => new Date());
  const [gepland, setGepland] = useState([]);
  const [ongepland, setOngepland] = useState([]);
  const [q, setQ] = useState("");
  const [drop, setDrop] = useState(null); // maand-weergave tijdkeuze
  const [faseFilter, setFaseFilter] = useState([]);
  const [now, setNow] = useState(() => new Date());
  const [selectMode, setSelectMode] = useState(false);
  const [selectie, setSelectie] = useState(() => new Set());
  const [batch, setBatch] = useState(null); // { datum, tijd, modus, interval }
  const [faseOpties, setFaseOpties] = useState([]);
  const [achterstallig, setAchterstallig] = useState([]);
  const scrollRef = useRef(null);
  const dragInfo = useRef({ id: null, grab: 0 });
  const scrolledRef = useRef(false);

  // "Nu"-lijn elke minuut verversen
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 60000); return () => clearInterval(t); }, []);

  let rangeStart, rangeEnd, gridDays;
  if (view === "dag") {
    rangeStart = new Date(anchor); rangeStart.setHours(0, 0, 0, 0);
    rangeEnd = addDays(rangeStart, 1); gridDays = [new Date(rangeStart)];
  } else if (view === "week") {
    rangeStart = startOfWeek(anchor); rangeEnd = addDays(rangeStart, 7);
    gridDays = Array.from({ length: 7 }, (_, i) => addDays(rangeStart, i));
  } else {
    const mStart = startOfMonth(anchor);
    rangeStart = startOfWeek(mStart);
    gridDays = Array.from({ length: 42 }, (_, i) => addDays(rangeStart, i));
    rangeEnd = addDays(rangeStart, 42);
  }

  const load = useCallback(async () => {
    const [{ data }, aRes] = await Promise.all([
      api.get("/planner/agenda", { params: { start: rangeStart.toISOString(), eind: rangeEnd.toISOString() } }),
      api.get("/planner/achterstallig"),
    ]);
    setGepland(data.gepland || []);
    setOngepland(data.ongepland || []);
    setAchterstallig(aRes.data.achterstallig || []);
  }, [view, anchor.getTime()]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  // Fasefilter-opties uit de werkelijke data (+ Beltaak)
  useEffect(() => {
    api.get("/verkoopkansen/fases").then(({ data }) => {
      const lijst = (data || []).map((x) => x.fase).filter(Boolean);
      if (!lijst.includes("Beltaak")) lijst.push("Beltaak");
      setFaseOpties(lijst);
    }).catch(() => setFaseOpties(FASES));
  }, []);

  // Autoscroll naar ~07:00 bij openen van dag/week
  useEffect(() => {
    if ((view === "dag" || view === "week") && scrollRef.current && !scrolledRef.current) {
      scrollRef.current.scrollTop = 6.5 * HOUR_H;
      scrolledRef.current = true;
    }
    if (view === "maand") scrolledRef.current = false;
  }, [view, gepland]);

  const allKansen = [...gepland, ...ongepland];
  const faseOk = (k) => faseFilter.length === 0 || faseFilter.includes(k.fase);
  const toggleFase = (f) => setFaseFilter((cur) => cur.includes(f) ? cur.filter((x) => x !== f) : [...cur, f]);

  const inPeriode = gepland.filter((k) => {
    if (!k.gepland_op) return false;
    const d = new Date(k.gepland_op);
    return d >= rangeStart && d < rangeEnd && faseOk(k);
  });
  const faseLijst = faseOpties.length ? faseOpties : FASES;
  const perFase = faseLijst.map((f) => ({ fase: f, n: inPeriode.filter((k) => k.fase === f).length })).filter((x) => x.n > 0);

  const reschedule = async (id, dateObj, h, m) => {
    const dt = new Date(dateObj); dt.setHours(h, m, 0, 0);
    const item = allKansen.find((k) => k.id === id);
    if (!item) return;
    try {
      if (item.type === "beltaak") await api.patch(`/beltaken/${id}`, { plan_datum: dt.toISOString() });
      else await api.patch(`/verkoopkansen/${id}`, { gepland_op: dt.toISOString() });
      const tl = dt.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" });
      toast.success(`Verzet naar ${dt.toLocaleDateString("nl-NL", { weekday: "short", day: "numeric", month: "short" })} ${tl}`);
      load();
    } catch { toast.error("Verzetten mislukt"); }
  };
  const unschedule = async (id) => {
    const item = allKansen.find((k) => k.id === id);
    if (item?.type === "beltaak") return;
    try { await api.patch(`/verkoopkansen/${id}`, { gepland_op: null }); toast.success("Uit planning gehaald"); load(); }
    catch { toast.error("Mislukt"); }
  };
  const bevestigDrop = async () => {
    if (!drop) return;
    const [h, m] = (drop.tijd || "09:00").split(":").map(Number);
    await reschedule(drop.id, drop.date, h || 0, m || 0);
    setDrop(null);
  };

  // --- Meervoudige selectie & batch-acties ---
  const isSel = (id) => selectie.has(id);
  const toggleSel = (id) => setSelectie((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const clearSel = () => setSelectie(new Set());
  const exitSelect = () => { setSelectMode(false); clearSel(); };
  const startBelmodus = () => {
    const actief = faseFilter.length ? faseFilter : ["Cold", "Terugbellen", "Beltaak"];
    const param = actief.join(",");
    navigate(`/agenda/bellen?fases=${encodeURIComponent(param)}&scope=vandaag`);
  };
  const planAchterstalligVandaag = async () => {
    const lijst = achterstallig.filter(faseOk);
    if (!lijst.length) return;
    const items = lijst.map((k) => {
      const orig = new Date(k.gepland_op);
      const dt = new Date(); dt.setHours(orig.getHours() || 9, orig.getMinutes() || 0, 0, 0);
      return { id: k.id, type: k.type, datum: dt.toISOString() };
    });
    try { await api.post("/planner/batch-plan", { items }); toast.success(`${items.length} naar vandaag verplaatst`); load(); }
    catch { toast.error("Mislukt"); }
  };
  const toDateInput = (d) => { const x = new Date(d); return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`; };

  const doBatchPlan = async () => {
    if (!batch) return;
    const base = batch.datum ? new Date(`${batch.datum}T00:00:00`) : new Date();
    const [h, m] = (batch.tijd || "09:00").split(":").map(Number);
    const step = Math.max(parseInt(batch.interval, 10) || 15, 1);
    const items = [...selectie].map((id, i) => {
      const it = allKansen.find((k) => k.id === id);
      if (!it) return null;
      const dt = new Date(base);
      dt.setHours(h || 0, (m || 0) + (batch.modus === "zelfde" ? 0 : step * i), 0, 0);
      return { id, type: it.type, datum: dt.toISOString() };
    }).filter(Boolean);
    if (!items.length) return;
    try {
      await api.post("/planner/batch-plan", { items });
      toast.success(`${items.length} afspraken ingepland op ${base.toLocaleDateString("nl-NL", { weekday: "short", day: "numeric", month: "short" })}`);
      setBatch(null); clearSel(); load();
    } catch { toast.error("Batch-inplannen mislukt"); }
  };

  const shiftDays = async (delta) => {
    const items = [...selectie].map((id) => {
      const it = allKansen.find((k) => k.id === id);
      if (!it || !it.gepland_op) return null; // alleen reeds geplande items verschuiven
      const dt = new Date(it.gepland_op); dt.setDate(dt.getDate() + delta);
      return { id, type: it.type, datum: dt.toISOString() };
    }).filter(Boolean);
    if (!items.length) { toast.error("Selecteer eerst geplande afspraken"); return; }
    try {
      await api.post("/planner/batch-plan", { items });
      toast.success(`${items.length} verschoven (${delta > 0 ? "+" : ""}${delta} dag)`);
      load();
    } catch { toast.error("Verschuiven mislukt"); }
  };

  const batchUnschedule = async () => {
    const items = [...selectie].map((id) => {
      const it = allKansen.find((k) => k.id === id);
      if (!it || it.type === "beltaak") return null;
      return { id, type: "kans", datum: null };
    }).filter(Boolean);
    if (!items.length) { toast.error("Alleen verkoopkansen kunnen uit de planning"); return; }
    try {
      await api.post("/planner/batch-plan", { items });
      toast.success(`${items.length} uit planning gehaald`);
      clearSel(); load();
    } catch { toast.error("Mislukt"); }
  };

  const openItem = (k) => navigate(k.type === "beltaak" ? `/bellen/modus?taak=${k.id}` : `/verkoopkansen/${k.id}`);
  const tijdTekst = (k) => new Date(k.gepland_op).toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" });
  const subTekst = (k) => (k.type === "beltaak" ? (k.campagne_naam || "Bellen") : (k.naam || `${k.duur_minuten || 3} min`));

  const isAllDay = (k) => { const d = new Date(k.gepland_op); return d.getHours() === 0 && d.getMinutes() === 0; };
  const timedForDay = (dateObj) => {
    const items = gepland.filter((k) => k.gepland_op && faseOk(k) && sameDay(new Date(k.gepland_op), dateObj) && !isAllDay(k))
      .map((k) => {
        const d = new Date(k.gepland_op); const start = minutesOf(d);
        return { ...k, _start: start, _end: Math.min(start + Math.max(k.duur_minuten || 3, 15), 24 * 60) };
      });
    return packEvents(items);
  };
  const allDayForDay = (dateObj) => gepland.filter((k) => k.gepland_op && faseOk(k) && sameDay(new Date(k.gepland_op), dateObj) && isAllDay(k));
  const opDag = (dateObj) => gepland.filter((k) => k.gepland_op && faseOk(k) && sameDay(new Date(k.gepland_op), dateObj))
    .sort((a, b) => new Date(a.gepland_op) - new Date(b.gepland_op));

  const filteredOngepland = ongepland.filter((k) =>
    faseOk(k) && (!q || (k.naam || "").toLowerCase().includes(q.toLowerCase()) || (k.organisatie || "").toLowerCase().includes(q.toLowerCase())));

  // --- Drag & drop ---
  const onEventDragStart = (k) => (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    dragInfo.current = { id: k.id, grab: e.clientY - rect.top };
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", k.id);
  };
  const onSidebarDragStart = (k) => (e) => {
    dragInfo.current = { id: k.id, grab: 0 };
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", k.id);
  };
  const onColumnDrop = (dateObj) => (e) => {
    e.preventDefault();
    const info = dragInfo.current;
    const id = info.id || e.dataTransfer.getData("text/plain");
    if (!id) return;
    const rect = e.currentTarget.getBoundingClientRect();
    let min = Math.round(((e.clientY - rect.top - info.grab) / HOUR_H * 60) / 15) * 15;
    min = Math.max(0, Math.min(min, 24 * 60 - 15));
    reschedule(id, dateObj, Math.floor(min / 60), min % 60);
    dragInfo.current = { id: null, grab: 0 };
  };
  const onAllDayDrop = (dateObj) => (e) => {
    e.preventDefault();
    const id = dragInfo.current.id || e.dataTransfer.getData("text/plain");
    if (id) reschedule(id, dateObj, 0, 0);
    dragInfo.current = { id: null, grab: 0 };
  };
  const onMonthDrop = (dateObj) => (e) => {
    e.preventDefault();
    const id = dragInfo.current.id || e.dataTransfer.getData("text/plain");
    if (id) setDrop({ id, date: new Date(dateObj), tijd: "09:00" });
    dragInfo.current = { id: null, grab: 0 };
  };
  const allowDrop = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };

  const prev = () => setAnchor(view === "dag" ? addDays(anchor, -1) : view === "week" ? addDays(anchor, -7) : addMonths(anchor, -1));
  const next = () => setAnchor(view === "dag" ? addDays(anchor, 1) : view === "week" ? addDays(anchor, 7) : addMonths(anchor, 1));
  const periodeLabel = () => {
    if (view === "dag") return anchor.toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    if (view === "week") { const s = startOfWeek(anchor), e = addDays(s, 6); return `Week ${isoWeek(s)} · ${s.getDate()} ${MAANDEN[s.getMonth()]} – ${e.getDate()} ${MAANDEN[e.getMonth()]} ${e.getFullYear()}`; }
    return `${MAANDEN_VOL[anchor.getMonth()]} ${anchor.getFullYear()}`;
  };

  const gridTemplateColumns = `58px repeat(${gridDays.length}, minmax(0, 1fr))`;

  // --- Afspraakblok (dag/week) ---
  const EventBlock = ({ k }) => {
    const c = fc(k.fase);
    const top = (k._start / 60) * HOUR_H;
    const height = Math.max((k._end - k._start) / 60 * HOUR_H, MIN_BLOCK);
    const width = 100 / (k._cols || 1);
    const left = (k._col || 0) * width;
    const isBel = k.type === "beltaak";
    return (
      <div
        draggable onDragStart={onEventDragStart(k)}
        onClick={(e) => { e.stopPropagation(); selectMode ? toggleSel(k.id) : openItem(k); }}
        style={{ top, height, left: `${left}%`, width: `calc(${width}% - 3px)` }}
        className={`group absolute rounded-lg border ${c.bg} ${c.bd} ${c.tx} overflow-hidden cursor-pointer shadow-sm hover:shadow-md hover:z-20 transition-shadow ${isSel(k.id) ? "ring-2 ring-primary ring-offset-1 z-20" : ""}`}
        data-testid={`planner-kaart-${k.id}`}
        title={`${tijdTekst(k)} — ${k.organisatie || k.naam}${isBel && k.telefoon ? ` · ${k.telefoon}` : ""}`}>
        <div className={`absolute left-0 top-0 bottom-0 w-1 ${c.bar}`} />
        <div className="pl-2.5 pr-1 py-0.5 h-full">
          <div className="flex items-center gap-1 text-[11px] font-semibold leading-tight truncate">
            {isBel && <Phone className="h-3 w-3 shrink-0" />}
            <span className="truncate">{k.organisatie || k.naam || "Afspraak"}</span>
          </div>
          {height >= 34 && <div className="text-[10px] opacity-80 truncate">{tijdTekst(k)} · {subTekst(k)}</div>}
        </div>
        {!isBel && (
          <button onClick={(e) => { e.stopPropagation(); unschedule(k.id); }}
            className="absolute top-0.5 right-0.5 opacity-0 group-hover:opacity-100 rounded p-0.5 hover:bg-black/10"
            data-testid={`planner-unschedule-${k.id}`}><X className="h-3 w-3" /></button>
        )}
      </div>
    );
  };

  const SidebarCard = ({ k }) => {
    const c = fc(k.fase);
    const isBel = k.type === "beltaak";
    return (
      <div draggable onDragStart={onSidebarDragStart(k)} onClick={() => selectMode ? toggleSel(k.id) : openItem(k)}
        className={`group flex items-start gap-2 rounded-lg border bg-card px-2.5 py-2 cursor-grab active:cursor-grabbing hover:shadow-sm transition-shadow ${isSel(k.id) ? "border-primary ring-1 ring-primary bg-primary/5" : c.bd}`}
        data-testid={`planner-kaart-${k.id}`}
        title={`${k.organisatie || k.naam}`}>
        <span className={`mt-1 h-2.5 w-2.5 rounded-full shrink-0 ${c.dot}`} />
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold truncate">{k.organisatie || k.naam}</div>
          <div className="text-[11px] text-muted-foreground truncate flex items-center gap-1">
            {isBel ? <><Phone className="h-3 w-3" />{k.campagne_naam || "Bellen"}</> : (k.naam || k.fase)}
          </div>
        </div>
        {selectMode
          ? <span className={`shrink-0 mt-0.5 h-4 w-4 rounded border flex items-center justify-center ${isSel(k.id) ? "bg-primary border-primary text-primary-foreground" : "border-muted-foreground/40"}`}>{isSel(k.id) && <Check className="h-3 w-3" />}</span>
          : <GripVertical className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 shrink-0 mt-0.5" />}
      </div>
    );
  };

  return (
    <div className="space-y-5" data-testid="campagne-planner">
      {/* Kop */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center"><CalendarDays className="h-6 w-6 text-primary" /></div>
          <div>
            <h1 className="font-display text-3xl font-medium tracking-tight">Agenda</h1>
            <p className="text-sm text-muted-foreground">Sleep afspraken om te verzetten. Klik een belkaart om te bellen.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button onClick={startBelmodus} data-testid="agenda-belmodus"><PhoneCall className="h-4 w-4 mr-1" />Belmodus</Button>
          <Button variant={selectMode ? "default" : "outline"} onClick={() => selectMode ? exitSelect() : setSelectMode(true)} data-testid="agenda-select-toggle"><ListChecks className="h-4 w-4 mr-1" />{selectMode ? "Klaar" : "Selecteren"}</Button>
          <div className="flex items-center gap-1 rounded-lg bg-secondary p-1" data-testid="agenda-view-switch">
            {["dag", "week", "maand"].map((v) => (
              <button key={v} onClick={() => setView(v)} data-testid={`agenda-view-${v}`}
                className={`rounded-md px-3 py-1.5 text-xs font-medium capitalize transition-colors ${view === v ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>{v}</button>
            ))}
          </div>
          <Button variant="outline" size="icon" onClick={prev} data-testid="planner-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" size="icon" onClick={next} data-testid="planner-next"><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="secondary" onClick={() => setAnchor(new Date())} data-testid="planner-today">Vandaag</Button>
        </div>
      </div>

      {/* Periode + samenvatting */}
      <div className="bg-card border border-border rounded-xl px-4 py-3 flex flex-wrap items-center justify-between gap-3" data-testid="agenda-samenvatting">
        <div className="font-display text-lg font-medium">{periodeLabel()}</div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">{inPeriode.length} ingepland:</span>
          {perFase.length === 0 && <span className="text-xs text-muted-foreground italic">geen</span>}
          {perFase.map(({ fase, n }) => (
            <span key={fase} className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-2.5 py-1 text-xs" data-testid={`samenvatting-fase-${fase}`}>
              <span className={`h-2.5 w-2.5 rounded-full ${fc(fase).dot}`} />{fase} <strong>{n}</strong>
            </span>
          ))}
        </div>
      </div>

      {/* Fase-filter */}
      <div className="flex flex-wrap items-center gap-2" data-testid="planner-fase-filter">
        <span className="text-xs text-muted-foreground mr-1">Filter op fase:</span>
        {faseLijst.map((f) => {
          const actief = faseFilter.includes(f);
          return (
            <button key={f} onClick={() => toggleFase(f)} data-testid={`planner-fase-${f}`}
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors ${actief ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground hover:bg-secondary"}`}>
              <span className={`h-2.5 w-2.5 rounded-full ${fc(f).dot}`} />{f}
            </button>
          );
        })}
        {faseFilter.length > 0 && <button onClick={() => setFaseFilter([])} className="text-xs text-primary hover:underline ml-1" data-testid="planner-fase-reset">Wis filter</button>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
        <div className="space-y-4">
        {achterstallig.filter(faseOk).length > 0 && (
          <div className="bg-card border border-rose-300 rounded-xl p-3" data-testid="planner-achterstallig">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-sm text-rose-700">Achterstallig</span>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="border-rose-300 text-rose-700">{achterstallig.filter(faseOk).length}</Badge>
                <button onClick={planAchterstalligVandaag} className="text-xs text-rose-700 hover:underline font-medium" data-testid="achterstallig-naar-vandaag">→ vandaag</button>
              </div>
            </div>
            <div className="space-y-1.5 overflow-y-auto max-h-[30vh] pr-1">
              {achterstallig.filter(faseOk).map((k) => <SidebarCard key={k.id} k={k} />)}
            </div>
          </div>
        )}
        {/* Zijbalk ongepland */}
        <div className="bg-card border border-border rounded-xl p-3 flex flex-col"
          onDragOver={allowDrop}
          onDrop={(e) => { e.preventDefault(); const id = dragInfo.current.id || e.dataTransfer.getData("text/plain"); if (id) unschedule(id); dragInfo.current = { id: null, grab: 0 }; }}
          data-testid="planner-ongepland">
          <div className="flex items-center justify-between mb-2">
            <span className="font-medium text-sm">Ongepland</span>
            <Badge variant="outline">{filteredOngepland.length}</Badge>
          </div>
          <div className="relative mb-2">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input className="pl-8 h-8 text-sm" placeholder="Zoeken…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="planner-zoek" />
          </div>
          <div className="space-y-1.5 overflow-y-auto max-h-[62vh] pr-1">
            {filteredOngepland.map((k) => <SidebarCard key={k.id} k={k} />)}
            {filteredOngepland.length === 0 && <div className="text-xs text-muted-foreground text-center py-6">Niets ongepland.</div>}
          </div>
        </div>
        </div>

        {/* Rooster */}
        {view === "maand" ? (
          <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="agenda-maand">
            <div className="grid grid-cols-7 border-b border-border bg-secondary/40">
              {DAGEN.map((d) => <div key={d} className="px-2 py-2 text-center text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{d}</div>)}
            </div>
            <div className="grid grid-cols-7">
              {gridDays.map((d, i) => {
                const inMonth = d.getMonth() === anchor.getMonth();
                const isToday = sameDay(d, new Date());
                const items = opDag(d);
                return (
                  <div key={d.toDateString()} onDragOver={allowDrop} onDrop={onMonthDrop(d)}
                    onClick={() => { if (selectMode) return; setView("dag"); setAnchor(new Date(d)); }}
                    className={`border-b border-r border-border min-h-[108px] p-1.5 align-top transition-colors hover:bg-secondary/20 ${inMonth ? "" : "bg-secondary/20 text-muted-foreground"}`}
                    data-testid={`agenda-maand-cel-${i}`}>
                    <div className="flex justify-end mb-1">
                      <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs ${isToday ? "bg-primary text-primary-foreground font-semibold" : inMonth ? "text-foreground" : "text-muted-foreground"}`}>{d.getDate()}</span>
                    </div>
                    <div className="space-y-1">
                      {items.slice(0, 3).map((k) => {
                        const c = fc(k.fase);
                        return (
                          <div key={k.id} draggable onDragStart={onSidebarDragStart(k)}
                            onClick={(e) => { e.stopPropagation(); selectMode ? toggleSel(k.id) : openItem(k); }}
                            className={`flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium cursor-pointer ${c.bg} ${c.tx} truncate ${isSel(k.id) ? "ring-1 ring-primary" : ""}`}
                            data-testid={`planner-kaart-${k.id}`} title={`${tijdTekst(k)} — ${k.organisatie || k.naam}`}>
                            <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${c.dot}`} />
                            <span className="shrink-0 opacity-70">{tijdTekst(k)}</span>
                            <span className="truncate">{k.organisatie || k.naam}</span>
                          </div>
                        );
                      })}
                      {items.length > 3 && <div className="text-[10px] text-muted-foreground pl-1">+{items.length - 3} meer</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div ref={scrollRef} className="bg-card border border-border rounded-xl overflow-y-auto max-h-[74vh] relative" data-testid={`agenda-${view}`}>
            {/* Dag-koppen (sticky) */}
            <div className="sticky top-0 z-30 grid bg-card/95 backdrop-blur border-b border-border" style={{ gridTemplateColumns }}>
              <div className="h-14 border-r border-border" />
              {gridDays.map((d) => {
                const isToday = sameDay(d, new Date());
                return (
                  <button key={d.toDateString()} onClick={() => { setView("dag"); setAnchor(new Date(d)); }}
                    className={`h-14 border-r border-border flex flex-col items-center justify-center transition-colors hover:bg-secondary/40 ${isToday ? "bg-primary/5" : ""}`}>
                    <span className="text-[11px] uppercase tracking-wide text-muted-foreground">{DAGEN[(d.getDay() + 6) % 7]}</span>
                    <span className={`font-display text-lg leading-none mt-0.5 inline-flex h-7 w-7 items-center justify-center rounded-full ${isToday ? "bg-primary text-primary-foreground font-semibold" : ""}`}>{d.getDate()}</span>
                  </button>
                );
              })}
            </div>

            {/* Hele-dag-rij */}
            <div className="grid border-b border-border bg-secondary/10" style={{ gridTemplateColumns }}>
              <div className="border-r border-border flex items-center justify-end pr-1.5 py-1 text-[10px] text-muted-foreground">hele dag</div>
              {gridDays.map((d) => (
                <div key={d.toDateString()} onDragOver={allowDrop} onDrop={onAllDayDrop(d)}
                  className="border-r border-border min-h-[30px] p-1 space-y-1">
                  {allDayForDay(d).map((k) => {
                    const c = fc(k.fase);
                    return (
                      <div key={k.id} draggable onDragStart={onSidebarDragStart(k)}
                        onClick={(e) => { e.stopPropagation(); selectMode ? toggleSel(k.id) : openItem(k); }}
                        className={`flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium cursor-pointer ${c.bg} ${c.tx} ${c.bd} border truncate ${isSel(k.id) ? "ring-1 ring-primary" : ""}`}
                        data-testid={`planner-kaart-${k.id}`} title={k.organisatie || k.naam}>
                        {k.type === "beltaak" && <Phone className="h-2.5 w-2.5 shrink-0" />}
                        <span className="truncate">{k.organisatie || k.naam}</span>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>

            {/* Tijdrooster */}
            <div className="grid" style={{ gridTemplateColumns, height: 24 * HOUR_H }}>
              {/* Tijd-gutter */}
              <div className="relative border-r border-border">
                {Array.from({ length: 24 }, (_, h) => (
                  <div key={h} className="absolute right-1.5 -translate-y-1/2 text-[10px] text-muted-foreground tabular-nums" style={{ top: h * HOUR_H }}>
                    {h === 0 ? "" : `${String(h).padStart(2, "0")}:00`}
                  </div>
                ))}
              </div>
              {/* Dagkolommen */}
              {gridDays.map((d) => {
                const isToday = sameDay(d, now);
                const nowTop = (minutesOf(now) / 60) * HOUR_H;
                return (
                  <div key={d.toDateString()} className="relative border-r border-border"
                    onDragOver={allowDrop} onDrop={onColumnDrop(d)}
                    style={{ backgroundImage: `repeating-linear-gradient(to bottom, hsl(var(--border)) 0, hsl(var(--border)) 1px, transparent 1px, transparent ${HOUR_H}px)` }}
                    data-testid={`agenda-dagkolom-${DAGEN[(d.getDay() + 6) % 7]}`}>
                    {timedForDay(d).map((k) => <EventBlock key={k.id} k={k} />)}
                    {isToday && (
                      <div className="absolute left-0 right-0 z-10 pointer-events-none" style={{ top: nowTop }}>
                        <div className="relative h-0 border-t-2 border-rose-500">
                          <span className="absolute -left-1 -top-1 h-2 w-2 rounded-full bg-rose-500" />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Tijdkeuze (maand-weergave) */}
      <Dialog open={!!drop} onOpenChange={(o) => { if (!o) setDrop(null); }}>
        <DialogContent className="max-w-sm" data-testid="planner-tijd-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Clock className="h-5 w-5 text-primary" /> Tijd kiezen</DialogTitle></DialogHeader>
          {drop && (() => {
            const dk = allKansen.find((k) => k.id === drop.id);
            return (
              <div className="space-y-3 text-sm">
                <div className="rounded-md bg-secondary/50 p-3">
                  <div className="font-medium">{dk?.organisatie || dk?.naam || "Afspraak"}</div>
                  {dk?.naam && dk?.organisatie && <div className="text-muted-foreground text-xs">{dk.naam}</div>}
                  <div className="text-muted-foreground text-xs mt-1">{drop.date.toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "long" })}</div>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">Tijdstip</Label>
                  <Select value={drop.tijd} onValueChange={(v) => setDrop({ ...drop, tijd: v })}>
                    <SelectTrigger data-testid="planner-tijd-input"><Clock className="h-4 w-4 mr-1 text-primary" /><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-64">{TIJD_SLOTS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDrop(null)}>Annuleren</Button>
            <Button onClick={bevestigDrop} data-testid="planner-tijd-bevestig">Inplannen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Batch-actiebalk */}
      {selectMode && selectie.size > 0 && (
        <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-50 flex items-center gap-1.5 rounded-2xl border border-border bg-card/95 backdrop-blur px-3 py-2 shadow-xl" data-testid="agenda-batch-bar">
          <span className="text-sm font-medium px-1 whitespace-nowrap">{selectie.size} geselecteerd</span>
          <div className="h-5 w-px bg-border mx-1" />
          <Button size="sm" onClick={() => setBatch({ datum: toDateInput(anchor), tijd: "09:00", modus: "spreiden", interval: "15" })} data-testid="agenda-batch-plan"><CalendarPlus className="h-4 w-4 mr-1" />Plan in op dag…</Button>
          <Button size="sm" variant="outline" onClick={() => shiftDays(-1)} data-testid="agenda-batch-min1">−1 dag</Button>
          <Button size="sm" variant="outline" onClick={() => shiftDays(1)} data-testid="agenda-batch-plus1">+1 dag</Button>
          <Button size="sm" variant="ghost" onClick={batchUnschedule} data-testid="agenda-batch-unschedule">Uit planning</Button>
          <Button size="sm" variant="ghost" onClick={clearSel} data-testid="agenda-batch-clear"><X className="h-4 w-4" /></Button>
        </div>
      )}

      {/* Batch inplannen op een dag */}
      <Dialog open={!!batch} onOpenChange={(o) => { if (!o) setBatch(null); }}>
        <DialogContent className="max-w-sm" data-testid="agenda-batch-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><CalendarPlus className="h-5 w-5 text-primary" /> {selectie.size} {selectie.size === 1 ? "afspraak" : "afspraken"} inplannen</DialogTitle></DialogHeader>
          {batch && (
            <div className="space-y-3 text-sm">
              <div>
                <Label className="text-xs text-muted-foreground mb-1 block">Op dag</Label>
                <div className="flex gap-1.5 mb-2">
                  {[["Vandaag", 0], ["Morgen", 1], ["Overmorgen", 2]].map(([lbl, off]) => {
                    const ymd = toDateInput(addDays(new Date(), off));
                    return (
                      <button key={lbl} onClick={() => setBatch({ ...batch, datum: ymd })}
                        className={`flex-1 rounded-md border px-2 py-1.5 text-xs transition-colors ${batch.datum === ymd ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground hover:bg-secondary"}`}
                        data-testid={`agenda-batch-snel-${off}`}>{lbl}</button>
                    );
                  })}
                </div>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start font-normal" data-testid="agenda-batch-datum">
                      <CalendarDays className="h-4 w-4 mr-2 text-primary" />{dagLabel(batch.datum)}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" weekStartsOn={1} initialFocus
                      selected={batch.datum ? new Date(`${batch.datum}T00:00:00`) : undefined}
                      onSelect={(d) => d && setBatch({ ...batch, datum: toDateInput(d) })} />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground mb-1 block">Starttijd</Label>
                <Select value={batch.tijd} onValueChange={(v) => setBatch({ ...batch, tijd: v })}>
                  <SelectTrigger data-testid="agenda-batch-tijd"><Clock className="h-4 w-4 mr-1 text-primary" /><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-64">{TIJD_SLOTS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground mb-1 block">Verdeling</Label>
                <div className="flex gap-2">
                  <button onClick={() => setBatch({ ...batch, modus: "spreiden" })} className={`flex-1 rounded-md border px-2 py-1.5 text-xs transition-colors ${batch.modus === "spreiden" ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground"}`} data-testid="agenda-batch-modus-spreiden">Achter elkaar</button>
                  <button onClick={() => setBatch({ ...batch, modus: "zelfde" })} className={`flex-1 rounded-md border px-2 py-1.5 text-xs transition-colors ${batch.modus === "zelfde" ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground"}`} data-testid="agenda-batch-modus-zelfde">Zelfde tijd</button>
                </div>
              </div>
              {batch.modus === "spreiden" && (
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">Tussentijd</Label>
                  <Select value={String(batch.interval)} onValueChange={(v) => setBatch({ ...batch, interval: v })}>
                    <SelectTrigger data-testid="agenda-batch-interval"><SelectValue /></SelectTrigger>
                    <SelectContent>{INTERVALLEN.map((n) => <SelectItem key={n} value={String(n)}>{n} minuten</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setBatch(null)}>Annuleren</Button>
            <Button onClick={doBatchPlan} data-testid="agenda-batch-bevestig">Inplannen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
